import { constants } from './constants';
import { AI_COMMERCIAL_PROV_ID, AI_COMMERCIAL_SOURCE } from './aiCommercial';
import { leadAttribution } from './attribution';

/**
 * CONFIGURACIÓN Y DATOS DE LA LANDING DEL KIT (/resources/ai-kit).
 *
 * Todo lo que el equipo tiene que rellenar vive AQUÍ, en un solo sitio, para que
 * cambiar una promoción o el origen de campaña no obligue a tocar la página.
 *
 * Regla de la página: nada que no esté confirmado se publica. Los valores
 * pendientes van VACÍOS y cada hueco tiene un camino honesto (sin promoción
 * confirmada, el bloque 4 pasa a «Empieza con InvokeApp» y «Ver planes», sin
 * urgencia). No los rellenes "provisionalmente": un corchete o un precio
 * inventado llega a producción tal cual.
 */

export const AI_KIT_PATH = '/resources/ai-kit';

/**
 * Integración de lead magnet que atiende este formulario.
 *
 * El `prov_id` identifica la UserIntegration en el core y de él salen la empresa,
 * el proyecto, la plantilla del contenido y el SMTP con el que se manda el kit.
 * Es PÚBLICO por diseño (el mismo formulario se incrusta en webs de clientes).
 * Cambiar el contenido del kit NO se hace aquí: se hace en la plantilla de esa
 * integración, dentro de la plataforma.
 */
export const AI_KIT_PROV_ID = 'prov_TubIvvATT_Hi84MRWIQN_g';

/** Origen que se graba en el lead (SourceLeads). Cámbialo por campaña. */
export const AI_KIT_SOURCE = 'webinar-15-sept';

/**
 * El MISMO popup sale en dos sitios y cada uno manda a su propia integración.
 *
 *   aikit -> lead-magnet-crm-email: crea el lead, DEVUELVE el contenido del kit y
 *            encola el correo. La confirmación puede hablar de "revisa tu email".
 *   home  -> lead-magnet-html-form: alta en la NEWSLETTER, no hay kit. SOLO crea
 *            el lead (no envía nada ni devuelve contenido), así que su
 *            confirmación no promete ningún correo. El formulario va con
 *            `variant="newsletter"` y manda siempre `enable_newsletter: true`.
 *
 * `delivers` es lo que decide qué dice la pantalla de gracias.
 */
export const LEAD_FORMS = {
  aikit: {
    endpoint: () => constants.leadMagnet,
    provId: AI_KIT_PROV_ID,
    source: AI_KIT_SOURCE,
    delivers: true,
    fallbackMessage: 'Solicitud del kit de IA',
  },
  /**
   * /resources/ai-commercial-system: su PROPIA integración lead-magnet-crm-email (prov_id del
   * dueño, 2026-10-20). Mismo JSON exacto que el kit. Ver `lib/aiCommercial.js`.
   */
  commercial: {
    endpoint: () => constants.leadMagnet,
    provId: AI_COMMERCIAL_PROV_ID,
    source: AI_COMMERCIAL_SOURCE,
    delivers: true,
    fallbackMessage: 'Solicitud del AI Commercial System',
  },
  home: {
    endpoint: () => constants.leadMagnetHtmlForm,
    provId: 'prov_2kRVbPb72bHCdSrwI0D4hA',
    source: 'newsletter-web',
    delivers: false,
    fallbackMessage: 'Alta en la newsletter (web)',
  },
};

/**
 * VISUALES REALES. Vacío = no se pinta nada.
 *
 * `kitPreview` tiene que ser una vista previa de los documentos que se entregan
 * DE VERDAD; mientras no exista, el bloque va sin imagen antes que con un
 * montaje.
 *
 * La captura del producto se QUITÓ a propósito: la que había (2024) no
 * representa el estado actual de la plataforma. En su sitio va el mensaje
 * rotatorio de la home, que dice lo mismo sin enseñar una pantalla vieja.
 */
export const aiKitVisuals = {
  kitPreview: '',            // p. ej. '/assets/img/core/kit-preview.png'
};

/**
 * OFERTA DEL WEBINAR.
 *
 * La cuenta atrás va contra una FECHA REAL y compartida (`expiresAt`), no contra
 * un contador que empieza cuando entra cada visitante: eso último es un truco y
 * además descuadra a quien vuelve. La fecha DEBE llevar escrito el desfase de
 * Madrid (+02:00 en verano, +01:00 en invierno).
 *
 * `plans` son las tarjetas que se ven DENTRO de esta página: son descuentos
 * exclusivos de aquí, así que no se manda a nadie a /pricing. Cada una lleva su
 * QR (imagen en public/assets/img/...) y/o su enlace de pago. Mientras la lista
 * esté vacía, el bloque enseña la cuenta atrás y dice que las condiciones se
 * anuncian en el webinar: ni un precio inventado.
 */
export const aiKitOffer = {
  active: true,
  // Hasta el VIERNES 18 a las 23:59, hora de Madrid: lo mismo que dicen el correo
  // del kit y el deck del webinar. Una línea y se mueve.
  // Con :59 de segundos, no :00 — "hasta las 23:59" incluye ese minuto entero; con
  // :00 la cuenta llegaría a cero justo al empezar el minuto que se ha anunciado.
  // La hora que se PINTA sigue siendo "23:59": offerDeadlineParts formatea solo
  // hora y minuto, y toLocaleTimeString trunca, no redondea.
  expiresAt: '2026-09-18T23:59:59+02:00',

  /**
   * LOS PLANES NO SE ESCRIBEN AQUÍ. Se leen del mismo endpoint público que
   * alimenta /pricing (`subscriptions/get-stripe-products`): nombre, bajada,
   * precio, logo y la lista de lo que incluye salen del core, así que si mañana
   * cambia un límite, cambia solo. Lo único que vive aquí es lo que el core NO
   * sabe: qué añade la promoción del webinar.
   *
   * La clave es el nombre del plan en minúsculas y sin guiones (seed, growth,
   * scaleup). Un plan sin entrada aquí se pinta con su precio normal y sin
   * etiquetas, que es justo lo que tiene que pasar con Scale-Up.
   */
  promos: {
    seed: {
      // Sin descuento: lo que suma es un agente que NO va en este plan.
      discountBadge: '',
      promoPrice: '',
      thenPrice: '',
      // Cada regalo lleva su PASTILLA (corta, para la fila de arriba) y su
      // explicación. Así se ve de un vistazo qué entra y, al leer, qué es.
      bonuses: [
        {
          pill: 'Recepcionista virtual',
          text: 'Recepcionista virtual incluido, que no va en el plan: lo configuras para cada '
              + 'cliente y puedes cobrarlo aparte todos los meses.',
        },
      ],
      code: '',
      qr: '',
      checkoutUrl: 'https://buy.stripe.com/dRm8wPcJWas1co71A51wY05',
    },
    growth: {
      discountBadge: '−150 € el primer mes',
      promoPrice: '249 €',
      thenPrice: '399 €/mes',
      bonuses: [
        {
          pill: '3 consultorías',
          text: '3 consultorías de acompañamiento: te ayudamos a montar tu agencia de '
              + 'inteligencia artificial.',
        },
        {
          pill: 'Proceso a medida',
          text: 'Desarrollamos o adaptamos un proceso del catálogo al tipo exacto de cliente '
              + 'de tu agencia, para que salgas con algo propio que vender.',
        },
      ],
      // El enlace de Stripe cobra el precio de lista: el descuento lo aplica el
      // CÓDIGO en el propio checkout. Por eso el código se enseña pegado al
      // botón y con su aviso, no escondido: sin él se pagarían 399 en vez de 249.
      code: 'WEBINAR15',
      qr: '',
      checkoutUrl: 'https://buy.stripe.com/cNi9ATcJW43Dewfa6B1wY04',
    },
    scaleup: {
      discountBadge: '',
      promoPrice: '',
      thenPrice: '',
      bonuses: [],
      code: '',
      qr: '',
      checkoutUrl: 'https://buy.stripe.com/00wdR925i1Vv5ZJ4Mh1wY06',
    },
  },

  // Tratamiento de impuestos: PENDIENTE de confirmar. Vacío = no se dice nada,
  // que es mejor que decir "IVA incluido" sin saberlo.
  taxNote: '',

  // Solo para cuando la promoción ya ha caducado: ahí sí, los planes públicos.
  endedPlansUrl: '/pricing',
};

/** Clave de promoción a partir del nombre del plan ('Scale-Up' -> 'scaleup'). */
export const promoKey = (name) =>
  String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Estado real de la oferta. Tres valores y ninguno más:
 *   'live'     -> se puede enseñar y contratar
 *   'expired'  -> hubo oferta y ya pasó (se cae a planes, sin urgencia)
 *   'inactive' -> no hay promoción confirmada (se cae a planes, sin urgencia)
 *
 * Que falte UN dato basta para que no sea 'live'. Es deliberado: media oferta
 * publicada es una promesa que el checkout no puede cumplir.
 */
export const offerState = (now = new Date()) => {
  const o = aiKitOffer;
  if (!o.active || !o.expiresAt) return 'inactive';
  const end = new Date(o.expiresAt);
  if (Number.isNaN(end.getTime())) return 'inactive';
  return end.getTime() > now.getTime() ? 'live' : 'expired';
};

/** Milisegundos que faltan para el cierre (0 si ya pasó o no hay fecha). */
export const offerRemainingMs = (now = new Date()) => {
  if (!aiKitOffer.expiresAt) return 0;
  const end = new Date(aiKitOffer.expiresAt);
  if (Number.isNaN(end.getTime())) return 0;
  return Math.max(0, end.getTime() - now.getTime());
};

/** Fecha y hora del cierre, siempre leídas en hora de Madrid. */
export const offerDeadlineParts = (lang = 'es') => {
  if (!aiKitOffer.expiresAt) return null;
  const end = new Date(aiKitOffer.expiresAt);
  if (Number.isNaN(end.getTime())) return null;
  const locale = lang === 'en' ? 'en-GB' : 'es-ES';
  const tz = 'Europe/Madrid';
  return {
    date: end.toLocaleDateString(locale, {
      timeZone: tz, day: 'numeric', month: 'long', year: 'numeric',
    }),
    time: end.toLocaleTimeString(locale, {
      timeZone: tz, hour: '2-digit', minute: '2-digit',
    }),
  };
};

/* ------------------------------------------------------------------ */
/* Envío del formulario                                                */
/* ------------------------------------------------------------------ */

/**
 * Registra la solicitud del kit contra la integración de lead magnet del core.
 *
 * Lo que hace el endpoint, y por qué la confirmación puede ser honesta:
 *   1. crea el LEAD en el CRM (con `enable_newsletter` = la casilla, que es el
 *      único consentimiento comercial: pedir el kit no da de alta en nada);
 *   2. renderiza la plantilla del kit y la DEVUELVE en la respuesta, así que el
 *      contenido existe aunque el correo falle;
 *   3. encola el correo y dice en qué estado ha quedado (`email_status`:
 *      queued | not_configured | queue_failed).
 *
 * Devuelve `{ ok, emailQueued, contentHtml }`. Solo se dice «recibirás el acceso
 * por email» cuando `emailQueued` es cierto.
 *
 * EL JSON ES EXACTAMENTE EL CONTRATO DE LA INTEGRACIÓN, ni un campo más:
 * first_name, email, ask_lead_type, privacy_policy, source, enable_newsletter y
 * prov_id. `privacy_policy` = la casilla obligatoria de los Términos marcada;
 * `enable_newsletter` = el consentimiento comercial (en el kit, su casilla
 * opcional; en la newsletter de la home, el propio alta: siempre true).
 *
 * RESERVA: si el endpoint no responde (caído, red del recinto), la solicitud se
 * guarda por `settings/contact-us`, que escribe en el buzón de siempre. En un
 * evento con un QR no se puede perder un lead; eso sí, por esa vía no hay ni
 * contenido ni correo, y la confirmación lo dice.
 */
export const submitKitRequest = async ({
  email, newsletter, terms, form = 'aikit',
}) => {
  const target = LEAD_FORMS[form] || LEAD_FORMS.aikit;
  const clean = String(email || '').trim();
  const payload = {
    first_name: '',
    email: clean,
    ask_lead_type: false,
    // Sale de la casilla OBLIGATORIA del formulario, no de un `true` fijo: si
    // no se marca, no se envía nada. Es la aceptación de los Términos.
    privacy_policy: Boolean(terms),
    source: target.source,
    enable_newsletter: Boolean(newsletter),
    prov_id: target.provId,
    // Página de entrada y referente (sin escribir nada en el navegador): ver lib/attribution.js.
    attribution: leadAttribution(),
  };

  let response = null;
  try {
    response = await fetch(target.endpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    response = null;                       // sin red hacia el core
  }

  if (response && response.ok) {
    const data = await response.json().catch(() => ({}));
    return {
      ok: true,
      emailQueued: target.delivers && data.email_status === 'queued',
      contentHtml: target.delivers ? (data.content_html || '') : '',
    };
  }

  // 400 CON `lead_created` (antes `lead_uuid`, que el core ya no devuelve a un
  // anónimo): el core YA ha creado el lead y ha fallado después (por ejemplo,
  // sin plantilla de contenido). Ahí NO se recurre a la reserva: el visitante ya
  // está registrado y duplicarlo en otro buzón solo ensucia. Va ANTES que el
  // email inválido: «Email template not found» también lleva la palabra.
  // 400 con email inválido: es culpa del dato, no del servicio.
  if (response && response.status === 400) {
    const data = await response.json().catch(() => ({}));
    if (data.lead_created || data.lead_uuid) {
      return { ok: true, emailQueued: false, contentHtml: '' };
    }
    if (String(data.error || '').toLowerCase().includes('email')) {
      return { ok: false, error: 'invalid' };
    }
  }

  try {
    const res = await fetch(constants.contactUs, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: clean,
        email: clean,
        message: target.fallbackMessage,
        raw: { ...payload, fallback_reason: response ? response.status : 'network' },
      }),
    });
    // `fallback`: solo ha llegado a un buzón (ni lead ni alta en la newsletter),
    // y la confirmación no puede decir lo contrario.
    if (res.ok) return { ok: true, fallback: true, emailQueued: false, contentHtml: '' };
  } catch (e) { /* cae al error de red */ }

  return { ok: false, error: 'network' };
};

/* ------------------------------------------------------------------ */
/* Estado del visitante                                                */
/* ------------------------------------------------------------------ */

// Cada landing lleva sus propias marcas: haber pedido el kit no significa haber pedido
// el recurso comercial (y viceversa). `scope` = 'aikit' (por defecto) | 'aics'.
const keysFor = (scope = 'aikit') => ({
  requested: `${scope}_requested`,
  popup: `${scope}_popup_seen`,
});

// Respaldo en memoria para quien bloquea el almacenamiento del sitio (leerlo
// lanza SecurityError). Antes `popupAlreadyShown` respondía «sí» en ese caso y
// el popup no salía NUNCA; así sale una vez por carga de página.
const memory = {};
const mem = (scope) => {
  if (!memory[scope]) memory[scope] = { requested: false, shown: false };
  return memory[scope];
};

export const hasRequestedKit = (scope = 'aikit') => {
  try { return localStorage.getItem(keysFor(scope).requested) === '1' || mem(scope).requested; } catch (e) { return mem(scope).requested; }
};
export const markKitRequested = (scope = 'aikit') => {
  mem(scope).requested = true;
  try { localStorage.setItem(keysFor(scope).requested, '1'); } catch (e) { /* almacenamiento bloqueado */ }
};
export const popupAlreadyShown = (scope = 'aikit') => {
  try { return sessionStorage.getItem(keysFor(scope).popup) === '1' || mem(scope).shown; } catch (e) { return mem(scope).shown; }
};
export const markPopupShown = (scope = 'aikit') => {
  mem(scope).shown = true;
  try { sessionStorage.setItem(keysFor(scope).popup, '1'); } catch (e) { /* almacenamiento bloqueado */ }
};

/**
 * ¿Hay otro diálogo abierto? Un popup automático no se abre encima de nada.
 *
 * Mirar solo `body.style.overflow` no basta: el chat de la web es un diálogo NO
 * modal que no bloquea el scroll, y el popup se abría encima y lo ocultaba
 * (`body.aik-modal-open` lo esconde) a mitad de conversación. Su lanzador sí
 * marca `aria-expanded`.
 */
export const isOtherDialogOpen = () => {
  if (typeof document === 'undefined') return false;
  if (document.body.style.overflow === 'hidden') return true;
  // Solo si el lanzador se VE: en la landing se oculta con display:none y puede
  // quedarse con aria-expanded="true" si se llegó con el chat abierto; contarlo
  // bloqueaba la apertura automática durante toda la visita.
  const launcher = document.querySelector('body > button[aria-haspopup="dialog"][aria-expanded="true"]');
  if (launcher && launcher.getClientRects().length > 0) return true;
  return Array.from(document.querySelectorAll('[role="dialog"], [aria-modal="true"]')).some((el) => (
    el.getClientRects().length > 0 && window.getComputedStyle(el).visibility !== 'hidden'
  ));
};

/** Validación de email del formulario (la de verdad la hace el servidor). */
export const isValidEmail = (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(value || '').trim());
